/**
 * Impacto Económico — traduce NRW a dinero accionable.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../api/client";
import { PageHeader } from "../layout/PageHeader";

interface EconomicImpact {
  perdida_anual_clp: number;
  recuperable_clp: number;
  recuperado_ytd_clp: number;
  costo_operativo_clp: number;
  roi_acumulado: number;
  payback_promedio_dias: number;
  por_dma: Array<{ dma_codigo: string; perdida_clp_mes: number; recuperable_clp_mes: number; roi: number }>;
  waterfall: Array<{ etapa: string; valor_clp: number }>;
}

function clp(n: number): string {
  if (Math.abs(n) >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

export function EconomicImpact(): JSX.Element {
  const q = useQuery<EconomicImpact>({
    queryKey: ["economic.impact"],
    queryFn: () => api<EconomicImpact>("/economic/impact"),
    staleTime: 60_000,
  });
  const e = q.data;

  return (
    <>
      <PageHeader
        eyebrow="Analítica NRW"
        title="Impacto Económico"
        subtitle="Pérdida monetizada, recuperable, ROI y payback por DMA."
      />
      {e && (
        <>
          <div className="card-grid" style={{ marginBottom: 16 }}>
            <div className="kpi kpi--danger">
              <span className="kpi__label">Pérdida anual</span>
              <span className="kpi__value">{clp(e.perdida_anual_clp)}</span>
              <span className="kpi__hint">CLP estimado</span>
            </div>
            <div className="kpi kpi--accent">
              <span className="kpi__label">Recuperable</span>
              <span className="kpi__value">{clp(e.recuperable_clp)}</span>
              <span className="kpi__hint">potencial anual</span>
            </div>
            <div className="kpi kpi--info">
              <span className="kpi__label">Recuperado YTD</span>
              <span className="kpi__value">{clp(e.recuperado_ytd_clp)}</span>
              <span className="kpi__hint">en lo que va del año</span>
            </div>
            <div className="kpi">
              <span className="kpi__label">ROI acumulado</span>
              <span className="kpi__value">×{e.roi_acumulado.toFixed(1)}</span>
              <span className="kpi__hint">payback {e.payback_promedio_dias} días</span>
            </div>
          </div>

          <section className="card" style={{ marginBottom: 12 }}>
            <div className="card__title">Cascada de valor (anual)</div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={e.waterfall} margin={{ top: 8, right: 8, bottom: 0, left: 10 }}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="etapa" stroke="var(--chart-axis)" tick={{ fontSize: 11 }} />
                  <YAxis
                    stroke="var(--chart-axis)"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => clp(v)}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--c-surface)",
                      border: "1px solid var(--c-border)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v: number) => [clp(v), "CLP"]}
                  />
                  <Bar dataKey="valor_clp" radius={[6, 6, 0, 0]}>
                    {e.waterfall.map((row, i) => (
                      <Cell
                        key={i}
                        fill={row.valor_clp < 0 ? "var(--c-danger)" : i === 2 ? "var(--c-success)" : "var(--c-primary-300)"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="card">
            <div className="card__title">Pérdida y recuperable por DMA (mensual)</div>
            <div className="table-wrapper" style={{ borderColor: "transparent" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>DMA</th>
                    <th className="numeric">Pérdida/mes</th>
                    <th className="numeric">Recuperable/mes</th>
                    <th className="numeric">ROI</th>
                  </tr>
                </thead>
                <tbody>
                  {e.por_dma.map((row) => (
                    <tr key={row.dma_codigo}>
                      <td><strong>{row.dma_codigo}</strong></td>
                      <td className="numeric">{clp(row.perdida_clp_mes)}</td>
                      <td className="numeric">{clp(row.recuperable_clp_mes)}</td>
                      <td className="numeric">
                        <span className="badge badge--accent">×{row.roi}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
